import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContextController } from "context/AppContext";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

import updateInterviewFeedback from "layouts/pages/events/actions/updateInterviewFeedback";

import { Stack, Tooltip, Menu, MenuItem, IconButton } from "@mui/material";
import {
  Info as InfoIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
  ThumbUpOffAlt as ThumbUpOffAltIcon,
  ThumbsUpDown as ThumbUpDownIcon,
  Edit as EditIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  EventBusy as EventBusyIcon,
} from "@mui/icons-material";

import MDTypography from "components/MDTypography";
import ApplicantActionModal from "layouts/pages/applicants/components/ApplicantActionModal";
import AddANoteModal from "layouts/pages/applicants/components/AddANoteModal";
import { calculateInterviewOverallFeedback } from "utils/helpers/events";
import GenericMoreButton from "components/GenericMoreButton";
import InterviewFeedbackButton from "components/InterviewFeedbackButton";

const thumbUpOptions = [
  {
    label: "None",
    color: "action",
    icon: <ThumbUpIcon fontSize="medium" color="action" />,
  },
  {
    label: "Yes",
    color: "success",
    icon: <ThumbUpIcon fontSize="medium" color="success" />,
  },
  {
    label: "Maybe",
    color: "warning",
    icon: <ThumbUpIcon fontSize="medium" color="warning" />,
  },
  {
    label: "No",
    color: "error",
    icon: <ThumbDownIcon fontSize="medium" color="error" />,
  },
];

const TeamMemberActions = ({
  id,
  parentComponent,
  row,
  onRemove,
  onChangeFeedback,
  selectedApplicant,
  isExternalMember,
  onEditExternalMember,
  onOpenNotes,
  event,
  notesDisabled,
  onToggleAvailabilityBlock,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { currentLoggedUser, currentEvent } = useAppContextController();
  const [isMailModalOpen, setMailModalOpen] = useState(false);
  const [isNotesModalOpen, setNotesModalOpen] = useState(false);
  const [toastAlert, setToastAlert] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isThumbsUpOpen = Boolean(anchorEl);

  const blocksAvailability = row?.interview?.blocksAvailability === "Yes";

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const thumbIcon = useMemo(() => {
    if (!selectedApplicant)
      return {
        label: "Select an applicant to show feedback",
        color: "action",
        icon: <ThumbUpOffAltIcon fontSize="medium" color="action" />,
      };
    const feedbackItem = selectedApplicant?.feedback?.find((fb) => fb.createAgent === id);
    if (!feedbackItem) {
      return thumbUpOptions[0];
    }
    const result = thumbUpOptions.find((option) => {
      return option.label === feedbackItem.rating;
    });
    if (result) return result;
    return thumbUpOptions[0];
  }, [id, selectedApplicant]);

  const tableActions = [
    ...(isExternalMember
      ? [
          {
            label: `${parentComponent} Info`,
            icon: (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditExternalMember?.();
                }}
              >
                <EditIcon fontSize="medium" color="info" />
              </IconButton>
            ),
          },
        ]
      : [
          {
            label: `${parentComponent} Info`,
            icon: (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/${parentComponent.toLowerCase()}s/${id}/action/${parentComponent.toLowerCase()}info`
                  );
                }}
              >
                <InfoIcon fontSize="medium" color="info" />
              </IconButton>
            ),
          },
        ]),
    {
      label: `Feedback`,
      noTooltip: true,
      icon: (
        <InterviewFeedbackButton
          applicant={selectedApplicant}
          user={row}
          onChangeFeedback={onChangeFeedback}
          eventUrl={currentEvent?.eventUrl}
          disableFinal
        />
      ),
    },
    {
      // eslint-disable-next-line no-nested-ternary
      label: selectedApplicant
        ? notesDisabled
          ? "No Notes"
          : "Notes"
        : "Select an applicant to show notes",
      icon: (
        <span>
          <IconButton
            sx={{ p: 0, opacity: selectedApplicant ? 1 : 0.5 }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenNotes(row);
            }}
            disabled={notesDisabled}
          >
            {selectedApplicant ? (
              <DescriptionIcon fontSize="medium" color={notesDisabled ? "secondary" : "info"} />
            ) : (
              <DescriptionOutlinedIcon fontSize="medium" color="secondary" />
            )}
          </IconButton>
        </span>
      ),
    },
    ...(isExternalMember
      ? []
      : [
          {
            label: blocksAvailability
              ? "User is unavailable for screening calls during this interview"
              : "User is available for screening calls during this interview",
            icon: (
              <IconButton
                sx={{ p: 0, opacity: blocksAvailability ? 1 : 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAvailabilityBlock(row, !blocksAvailability);
                }}
              >
                <EventBusyIcon
                  fontSize="medium"
                  color={blocksAvailability ? "error" : "secondary"}
                />
              </IconButton>
            ),
          },
        ]),
    {
      label: `Remove ${parentComponent}`,
      noTooltip: true,
      icon: (
        <IconButton
          sx={{ p: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <CancelIcon color="error" fontSize="medium" />
        </IconButton>
      ),
    },
  ];

  return (
    <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
      {tableActions.map((action) => (
        <>
          {action.noTooltip ? (
            action.icon
          ) : (
            <Tooltip title={action.label} placement="top">
              {action.icon}
            </Tooltip>
          )}
        </>
      ))}
    </Stack>
  );
};

export default TeamMemberActions;
